import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import ProtectedRoute from "components/ProtectedRoute";
import NotFound from "pages/NotFound";
import SignIn from "./pages/auth/SignIn";
import TechnicalInspection from './pages/technical-inspection';
import WorkOrderTimeline from './pages/work-order-timeline';
import SalesManagement from './pages/sales-management';
import QuotationManagement from './pages/quotation-management';
import WorkshopDashboard from './pages/workshop-dashboard';
import VehicleReception from './pages/vehicle-reception';
import AuditLog from './pages/audit-log';
import AdvancedReporting from './pages/advanced-reporting';
import ClientAnalytics from './pages/client-analytics';
import EmailManagement from './pages/email-management';
import EmailTemplateManagement from './pages/email-template-management';
import AutomationRulesManagement from './pages/automation-rules-management';
import AutomationAnalyticsDashboard from './pages/automation-analytics-dashboard';
import SupplierCoordination from './pages/supplier-coordination';
import WorkExecution from './pages/work-execution';
import VehicleDeliveryConfirmation from './pages/vehicle-delivery-confirmation';
import CustomerFeedback from './pages/customer-feedback';
import CompanyRegistration from './pages/company-registration';
import SubscriptionSuccess from 'pages/subscription-success/index';
import SaleNoteDetail from './pages/sale-note-detail';
import ProvidersManagement from './pages/providers-management';
import VehicleHistory from './pages/vehicle-history';
import CRMManagement from './pages/crm-management';
import OperationalCalendar from './pages/operational-calendar';
import VehicleArchive from './pages/vehicle-archive';
import QuoteAuthorization from './pages/quote-authorization';
import ClientsManagement from './pages/clients-management';
import ProductCatalog from './pages/product-catalog';
import CompanyBranding from './pages/company-branding';
import SolicitudesManagement from './pages/solicitudes-management';
import AccesosPortal from './pages/accesos-portal';
import QARoutes from './pages/qa-routes';
import QAActions from './pages/qa-actions';
import RedGearcontrol from './pages/red-gearcontrol';
// Portal imports
import { PortalAuthProvider } from './portal/PortalAuthContext';
import PortalProtectedRoute from './portal/PortalProtectedRoute';
import PortalLayout from './portal/PortalLayout';
import { AppointmentAlertProvider } from './contexts/AppointmentAlertContext';
// Portal protected wrapper (requires auth + layout)
const PortalRoute = ({ children }) => (
  <PortalAuthProvider>
    <PortalProtectedRoute>
      <PortalLayout>
        {children}
      </PortalLayout>
    </PortalProtectedRoute>
  </PortalAuthProvider>
);
// Portal public wrapper (auth context only, no redirect guard)
const PortalPublicRoute = ({ children }) => (
  <PortalAuthProvider>
    {children}
  </PortalAuthProvider>
);
const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <AppointmentAlertProvider>
          <RouterRoutes>
            {/* Public routes */}
            <Route path="/signin" element={<SignIn />} />
            {/* /signin/primer-acceso is no longer part of the login flow — redirect to signin */}
            <Route path="/signin/primer-acceso" element={<Navigate to="/signin" replace />} />
            <Route path="/company-registration" element={<CompanyRegistration />} />
            {/* Portal Cliente — public (no PortalProtectedRoute to avoid redirect loops) */}
            {/* Portal Cliente — protected */}
            {/* Protected internal routes */}
            <Route path="/" element={<Navigate to="/workshop-dashboard" replace />} />
            <Route path="/technical-inspection" element={<ProtectedRoute><TechnicalInspection /></ProtectedRoute>} />
            <Route path="/work-order-timeline" element={<ProtectedRoute><WorkOrderTimeline /></ProtectedRoute>} />
            <Route path="/sales-management" element={<ProtectedRoute><SalesManagement /></ProtectedRoute>} />
            <Route path="/quotation-management" element={<ProtectedRoute><QuotationManagement /></ProtectedRoute>} />
            <Route path="/workshop-dashboard" element={<ProtectedRoute><WorkshopDashboard /></ProtectedRoute>} />
            <Route path="/vehicle-reception" element={<ProtectedRoute><VehicleReception /></ProtectedRoute>} />
            <Route path="/audit-log" element={<ProtectedRoute><AuditLog /></ProtectedRoute>} />
            <Route path="/advanced-reporting" element={<ProtectedRoute><AdvancedReporting /></ProtectedRoute>} />
            <Route path="/client-analytics" element={<ProtectedRoute><ClientAnalytics /></ProtectedRoute>} />
            <Route path="/email-management" element={<ProtectedRoute><EmailManagement /></ProtectedRoute>} />
            <Route path="/email-template-agement" element={<ProtectedRoute><EmailTemplateManagement /></ProtectedRoute>} />
            <Route path="/automation-rules-management" element={<ProtectedRoute><AutomationRulesManagement /></ProtectedRoute>} />
            <Route path="/automation-analytics-dashboard" element={<ProtectedRoute><AutomationAnalyticsDashboard /></ProtectedRoute>} />
            <Route path="/supplier-coordination" element={<ProtectedRoute><SupplierCoordination /></ProtectedRoute>} />
            <Route path="/work-execution" element={<ProtectedRoute><WorkExecution /></ProtectedRoute>} />
            <Route path="/vehicle-delivery-confirmation" element={<ProtectedRoute><VehicleDeliveryConfirmation /></ProtectedRoute>} />
            <Route path="/customer-feedback" element={<ProtectedRoute><CustomerFeedback /></ProtectedRoute>} />
            <Route path="/sale-note-detail" element={<ProtectedRoute><SaleNoteDetail /></ProtectedRoute>} />
            <Route path="/providers-management" element={<ProtectedRoute><ProvidersManagement /></ProtectedRoute>} />
            <Route path="/vehicle-history" element={<ProtectedRoute><VehicleHistory /></ProtectedRoute>} />
            <Route path="/vehicle-history/:vehicle_id" element={<ProtectedRoute><VehicleHistory /></ProtectedRoute>} />
            <Route path="/crm-management" element={<ProtectedRoute><CRMManagement /></ProtectedRoute>} />
            <Route path="/operational-calendar" element={<ProtectedRoute><OperationalCalendar /></ProtectedRoute>} />
            <Route path="/vehicle-archive" element={<ProtectedRoute><VehicleArchive /></ProtectedRoute>} />
            <Route path="/authorize" element={<QuoteAuthorization />} />
            <Route path="/clients-management" element={<ProtectedRoute><ClientsManagement /></ProtectedRoute>} />
            <Route path="/product-catalog" element={<ProtectedRoute><ProductCatalog /></ProtectedRoute>} />
            <Route path="/company-branding" element={<ProtectedRoute><CompanyBranding /></ProtectedRoute>} />
            <Route path="/solicitudes-management" element={<ProtectedRoute><SolicitudesManagement /></ProtectedRoute>} />
            <Route path="/accesos-portal" element={<ProtectedRoute><AccesosPortal /></ProtectedRoute>} />
            <Route path="/qa/routes" element={<ProtectedRoute><QARoutes /></ProtectedRoute>} />
            <Route path="/qa/actions" element={<ProtectedRoute><QAActions /></ProtectedRoute>} />
            <Route path="/red-gearcontrol" element={<ProtectedRoute><RedGearcontrol /></ProtectedRoute>} />
            <Route path="/subscription-success" element={<SubscriptionSuccess />} />
            {/* Portal Cliente — public */}
            {/* Portal Cliente — protected */}
            <Route path="*" element={<NotFound />} />
          </RouterRoutes>
        </AppointmentAlertProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
};
export default Routes;
